class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.isClosing = false;
    this.closeTimer = null;
    this.swipeSettleTimer = null;
    this.suppressClickUntil = 0;
    this.resetSwipeGesture();

    this.addEventListener(
      'keyup',
      (event) => event.code === 'Escape' && this.close()
    );

    this.querySelector('#CartDrawer-Overlay')?.addEventListener(
      'click',
      this.close.bind(this)
    );

    this.setHeaderCartIconAccessibility();
    this.bindSwipeClose();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (
      this.classList.contains('active') ||
      this.isClosing
    ) {
      return;
    }

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    this.stopSwipeSettleTimer();
    this.clearSwipeStyles();
    this.resetSwipeGesture();

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    const drawerInner = this.querySelector('.drawer__inner');

    document.body.classList.add('overflow-hidden');

    /*
     * Същият ред на състоянията като при мобилното меню:
     * 1. drawer-ът се зарежда в затворена позиция;
     * 2. на следващия кадър започва отварянето.
     */
    this.classList.add('animate', 'active');
    drawerInner?.getBoundingClientRect();

    window.requestAnimationFrame(() => {
      if (
        !this.classList.contains('active') ||
        this.isClosing
      ) {
        return;
      }

      this.classList.add('cart-opening');
    });

    const finishOpen = (event) => {
      if (
        event &&
        (
          event.target !== drawerInner ||
          event.propertyName !== 'transform'
        )
      ) {
        return;
      }

      drawerInner?.removeEventListener(
        'transitionend',
        finishOpen
      );

      if (
        !this.classList.contains('active') ||
        this.isClosing
      ) {
        return;
      }

      const containerToTrapFocusOn =
        this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

      const focusElement =
        this.querySelector('.drawer__inner') ||
        this.querySelector('.drawer__close');

      trapFocus(
        containerToTrapFocusOn,
        focusElement
      );
    };

    drawerInner?.addEventListener(
      'transitionend',
      finishOpen
    );

    this.querySelector(
      'cart-drawer-items'
    )?.dispatchViewEvent();
  }

  close() {
    if (
      !this.classList.contains('active') ||
      this.isClosing
    ) {
      return;
    }

    const drawerInner =
      this.querySelector('.drawer__inner');

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    this.stopSwipeSettleTimer();
    this.clearSwipeStyles();
    this.resetSwipeGesture();

    this.isClosing = true;
    this.classList.remove('cart-opening');

    removeTrapFocus(this.activeElement);

    const finishClose = () => {
      if (!this.isClosing) return;

      drawerInner?.removeEventListener(
        'transitionend',
        handleTransitionEnd
      );

      this.finishCloseState();
    };

    const handleTransitionEnd = (event) => {
      if (
        event.target === drawerInner &&
        event.propertyName === 'transform'
      ) {
        finishClose();
      }
    };

    drawerInner?.addEventListener(
      'transitionend',
      handleTransitionEnd
    );

    /*
     * Същият прозорец за затваряне от 0.42 секунди
     * като при мобилното меню, плюс резервен таймер.
     */
    this.closeTimer = window.setTimeout(
      finishClose,
      reducedMotion ? 20 : 460
    );
  }

  finishCloseState() {
    this.classList.remove(
      'active',
      'animate',
      'cart-opening'
    );

    document.body.classList.remove(
      'overflow-hidden'
    );

    this.isClosing = false;

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    this.stopSwipeSettleTimer();
    this.clearSwipeStyles();
    this.resetSwipeGesture();
  }

  bindSwipeClose() {
    if (
      this.dataset.isitglowSwipeCloseReady === 'true'
    ) {
      return;
    }

    this.dataset.isitglowSwipeCloseReady = 'true';

    this.addEventListener(
      'touchstart',
      (event) => {
        const drawerInner =
          this.querySelector('.drawer__inner');

        if (
          !window.matchMedia(
            '(max-width: 749px)'
          ).matches ||
          !this.classList.contains('active') ||
          !this.classList.contains('cart-opening') ||
          this.isClosing ||
          event.touches.length !== 1 ||
          !drawerInner ||
          !event.target.closest('.drawer__inner')
        ) {
          this.resetSwipeGesture();
          return;
        }

        this.stopSwipeSettleTimer();
        this.clearSwipeStyles();

        const touch = event.touches[0];
        const now = window.performance.now();

        this.swipeStartX = touch.clientX;
        this.swipeStartY = touch.clientY;
        this.swipeCurrentX = this.swipeStartX;
        this.swipeCurrentY = this.swipeStartY;
        this.swipeLastX = this.swipeStartX;
        this.swipeLastMoveTime = now;
        this.swipeVelocityX = 0;
        this.swipeCurrentTranslate = 0;

        this.swipeDrawerWidth =
          drawerInner.getBoundingClientRect().width;

        this.swipeGestureActive =
          this.swipeDrawerWidth > 0;

        this.swipeHorizontal = false;
      },
      { passive: true }
    );

    this.addEventListener(
      'touchmove',
      (event) => {
        if (
          !this.swipeGestureActive ||
          event.touches.length !== 1
        ) {
          return;
        }

        const drawerInner =
          this.querySelector('.drawer__inner');

        const overlay =
          this.querySelector('#CartDrawer-Overlay');

        if (!drawerInner) {
          this.resetSwipeGesture();
          return;
        }

        const touch = event.touches[0];
        const now = window.performance.now();

        this.swipeCurrentX = touch.clientX;
        this.swipeCurrentY = touch.clientY;

        const deltaX =
          this.swipeCurrentX - this.swipeStartX;

        const deltaY =
          this.swipeCurrentY - this.swipeStartY;

        /*
         * Количката е отдясно, затова жестът
         * е огледален на менюто — надясно.
         */
        if (
          !this.swipeHorizontal &&
          Math.abs(deltaX) >= 10
        ) {
          if (
            deltaX > 0 &&
            Math.abs(deltaX) >
              Math.abs(deltaY) * 1.2
          ) {
            this.swipeHorizontal = true;

            drawerInner.style.setProperty(
              'transition',
              'none',
              'important'
            );

            drawerInner.style.setProperty(
              'will-change',
              'transform, opacity'
            );

            overlay?.style.setProperty(
              'transition',
              'none',
              'important'
            );

            overlay?.style.setProperty(
              'opacity',
              '1',
              'important'
            );
          } else if (
            Math.abs(deltaY) > Math.abs(deltaX) ||
            deltaX < 0
          ) {
            this.resetSwipeGesture();
            return;
          }
        }

        if (!this.swipeHorizontal) return;

        event.preventDefault();

        const elapsed =
          now - this.swipeLastMoveTime;

        if (elapsed > 0) {
          this.swipeVelocityX =
            (
              this.swipeCurrentX -
              this.swipeLastX
            ) / elapsed;
        }

        this.swipeLastX = this.swipeCurrentX;
        this.swipeLastMoveTime = now;

        this.swipeCurrentTranslate = Math.min(
          this.swipeDrawerWidth,
          Math.max(0, deltaX)
        );

        this.setSwipeDrawerPosition(
          this.swipeCurrentTranslate
        );
      },
      { passive: false }
    );

    this.addEventListener(
      'touchend',
      (event) => {
        if (
          !this.swipeGestureActive ||
          !this.swipeHorizontal
        ) {
          this.resetSwipeGesture();
          return;
        }

        if (event.changedTouches.length === 1) {
          this.swipeCurrentX =
            event.changedTouches[0].clientX;

          this.swipeCurrentY =
            event.changedTouches[0].clientY;
        }

        const now = window.performance.now();

        const progress =
          this.swipeDrawerWidth > 0
            ? Math.abs(
                this.swipeCurrentTranslate
              ) / this.swipeDrawerWidth
            : 0;

        if (
          now - this.swipeLastMoveTime > 100
        ) {
          this.swipeVelocityX = 0;
        }

        const returningLeft =
          this.swipeVelocityX < -0.18;

        const shouldClose =
          progress >= 0.72 ||
          (
            !returningLeft &&
            (
              progress >= 0.3 ||
              (
                this.swipeVelocityX > 0.45 &&
                progress > 0.08
              )
            )
          );

        const finalTranslate =
          this.swipeCurrentTranslate;

        const finalWidth =
          this.swipeDrawerWidth;

        event.preventDefault();

        this.suppressClickUntil =
          Date.now() + 500;

        this.resetSwipeGesture();

        if (shouldClose) {
          this.finishSwipeClose(
            finalTranslate,
            finalWidth
          );
        } else {
          this.returnSwipeToOpen();
        }
      },
      { passive: false }
    );

    this.addEventListener(
      'touchcancel',
      () => {
        const wasDragging =
          this.swipeGestureActive &&
          this.swipeHorizontal;

        this.resetSwipeGesture();

        if (wasDragging) {
          this.returnSwipeToOpen();
        }
      },
      { passive: true }
    );

    this.addEventListener(
      'click',
      (event) => {
        if (
          Date.now() < this.suppressClickUntil
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    );
  }

  setSwipeDrawerPosition(translateX) {
    const drawerInner =
      this.querySelector('.drawer__inner');

    const overlay =
      this.querySelector('#CartDrawer-Overlay');

    const progress =
      this.swipeDrawerWidth > 0
        ? Math.min(
            1,
            Math.abs(translateX) /
              this.swipeDrawerWidth
          )
        : 0;

    drawerInner?.style.setProperty(
      'transform',
      `translate3d(${translateX}px, 0, 0)`,
      'important'
    );

    drawerInner?.style.setProperty(
      'opacity',
      String(1 - progress * 0.04),
      'important'
    );

    overlay?.style.setProperty(
      'opacity',
      String(1 - progress),
      'important'
    );
  }

  returnSwipeToOpen() {
    const drawerInner =
      this.querySelector('.drawer__inner');

    const overlay =
      this.querySelector('#CartDrawer-Overlay');

    if (!drawerInner) return;

    this.stopSwipeSettleTimer();

    drawerInner.style.setProperty(
      'transition',
      'transform 0.34s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.24s ease',
      'important'
    );

    overlay?.style.setProperty(
      'transition',
      'opacity 340ms cubic-bezier(0.4, 0, 0.2, 1)',
      'important'
    );

    drawerInner.getBoundingClientRect();

    window.requestAnimationFrame(() => {
      drawerInner.style.setProperty(
        'transform',
        'translate3d(0, 0, 0)',
        'important'
      );

      drawerInner.style.setProperty(
        'opacity',
        '1',
        'important'
      );

      overlay?.style.setProperty(
        'opacity',
        '1',
        'important'
      );

      this.swipeSettleTimer =
        window.setTimeout(() => {
          this.clearSwipeStyles();
          this.swipeSettleTimer = null;
        }, 360);
    });
  }

  finishSwipeClose(translateX, width) {
    const drawerInner =
      this.querySelector('.drawer__inner');

    const overlay =
      this.querySelector('#CartDrawer-Overlay');

    if (
      !drawerInner ||
      width <= 0 ||
      this.isClosing
    ) {
      this.returnSwipeToOpen();
      return;
    }

    const progress = Math.min(
      1,
      Math.abs(translateX) / width
    );

    let duration = Math.round(
      400 * (1 - progress)
    );

    duration = Math.max(
      160,
      Math.min(360, duration)
    );

    this.stopSwipeSettleTimer();

    this.isClosing = true;

    removeTrapFocus(this.activeElement);

    drawerInner.style.setProperty(
      'transition',
      `transform ${duration}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${Math.max(
        140,
        duration - 60
      )}ms ease`,
      'important'
    );

    overlay?.style.setProperty(
      'transition',
      `opacity ${duration}ms cubic-bezier(0.4, 0, 0.2, 1)`,
      'important'
    );

    drawerInner.getBoundingClientRect();

    window.requestAnimationFrame(() => {
      drawerInner.style.setProperty(
        'transform',
        `translate3d(${width}px, 0, 0)`,
        'important'
      );

      drawerInner.style.setProperty(
        'opacity',
        '0.96',
        'important'
      );

      overlay?.style.setProperty(
        'opacity',
        '0',
        'important'
      );

      this.swipeSettleTimer =
        window.setTimeout(() => {
          this.finishCloseState();
        }, duration);
    });
  }

  stopSwipeSettleTimer() {
    if (!this.swipeSettleTimer) return;

    window.clearTimeout(
      this.swipeSettleTimer
    );

    this.swipeSettleTimer = null;
  }

  clearSwipeStyles() {
    const drawerInner =
      this.querySelector('.drawer__inner');

    const overlay =
      this.querySelector('#CartDrawer-Overlay');

    drawerInner?.style.removeProperty(
      'transition'
    );

    drawerInner?.style.removeProperty(
      'transform'
    );

    drawerInner?.style.removeProperty(
      'opacity'
    );

    drawerInner?.style.removeProperty(
      'will-change'
    );

    overlay?.style.removeProperty(
      'transition'
    );

    overlay?.style.removeProperty(
      'opacity'
    );
  }

  resetSwipeGesture() {
    this.swipeGestureActive = false;
    this.swipeHorizontal = false;
    this.swipeStartX = 0;
    this.swipeStartY = 0;
    this.swipeCurrentX = 0;
    this.swipeCurrentY = 0;
    this.swipeLastX = 0;
    this.swipeLastMoveTime = 0;
    this.swipeVelocityX = 0;
    this.swipeDrawerWidth = 0;
    this.swipeCurrentTranslate = 0;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote.nextElementSibling.getAttribute(
        'id'
      )
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        const isOpen = event.currentTarget
          .closest('details')
          .hasAttribute('open');

        event.currentTarget.setAttribute(
          'aria-expanded',
          !isOpen
        );
      }
    );

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    const drawerInner =
      this.querySelector('.drawer__inner');

    if (
      drawerInner.classList.contains('is-empty')
    ) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach(
      (section) => {
        const sectionElement = section.selector
          ? document.querySelector(
              section.selector
            )
          : document.getElementById(
              section.id
            );

        if (!sectionElement) return;

        sectionElement.innerHTML =
          this.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector
          );
      }
    );

    setTimeout(() => {
      this.querySelector(
        '#CartDrawer-Overlay'
      )?.addEventListener(
        'click',
        this.close.bind(this)
      );

      this.open();
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define(
  'cart-drawer',
  CartDrawer
);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define(
  'cart-drawer-items',
  CartDrawerItems
);